import { call, put, select, delay, takeLatest, all } from 'redux-saga/effects';
import {
  billingInfoLoaded,
  billingInfoLoadFailed,
  changeProfilePicture as CHANGE_PROFILE_PICTURE,
  changeProfilePictureFailed,
  changeProfilePictureSuccess,
  closeMultiPoliciesModal,
  coverPictureRemoved,
  emailUpdated,
  loadPaymentHistory,
  loadPoliciesPaymentNumber,
  loadReimbursementMethod,
  namesUpdated,
  updateNamesFailed,
  passwordUpdated,
  paymentHistoryLoaded,
  paymentHistoryLoadFailed,
  paymentMethodPastDueModalVisibleChanged,
  paymentMethodUpdateAttempted,
  clearUserCache,
  paymentMethodUpdated,
  petCloudEmailUpdated,
  petsLoadAttempt,
  petsLoaded,
  petsLoadedFinished,
  policyDetailsLoaded,
  policyDetailsLoadFailed,
  reimbursementMethodRoutingValidationFailed,
  reimbursementMethodUpdated,
  reimbursementMethodUpdateFailed,
  removeAvatarPicture as REMOVE_AVATAR_PICTURE,
  removeCoverPicture as REMOVE_COVER_PICTURE,
  removeCoverPictureFailed,
  showAlert,
  socialProfileLoadAttempt,
  socialProfileLoaded,
  socialProfileLoadedFinished,
  storeStates,
  updateEmailError,
  updatePasswordError,
  updatePetCloudEmailError,
  avatarPictureRemoved,
  removeAvatarPictureFailed,
  paymentHistoryCleared,
  updateInsuranceInformation as UPDATE_INSURANCE_INFORMATION,
  updateInsuranceInformationFailed,
  updateInsuranceInformationSuccess,
  displayConfirmChangesModal,
  getFeatureFlags as GET_FEATURE_FLAGS,
  getFeatureFlagsFailed,
  getFeatureFlagsLoaded,
  loadPolicyDetails,
  verifyCodeReset,
  stripeUpdatePaymentMethodError,
} from '../actions';
import {
  apiCallWithApiKey,
  del,
  get,
  getStorageOrDefault,
  IDOR_HEADER_V2,
  patch,
  post,
  postApiV2,
  postV2,
} from '../services/api';
import { isEmailValid } from '../services/utils';
import { IMAGE_UPLOAD_TYPE, REIMBURSEMENT_METHODS } from '../constants';
import {
  useClaimsApiV1ForSagas,
  useCustomerApi150617V1ForSagas,
  usePetCloudCareCreditReimbursementForSagas,
  usePetCloudRequireReimbursementVerificationForSagas,
  usePetCloudStripeT177101ForSagas,
  usePetCloudUpdatePaymentMethodT215309ForSagas,
} from '../services/featureFlagsForSagas';
import { getPolicySummary } from './policies';
import { USER_INFO_KEY } from '../services/constants';
import { MODAL_ALERT_TYPES } from '../components/common/CustomModal';

export function* getStates(dispatch) {
  const response = yield call(get, dispatch, 'api/State');
  if (response.success) {
    yield put(storeStates(response.Data));
  }
}

function* validateHomeDataChanged(type) {
  const {
    address1,
    address2,
    city,
    firstName,
    FirstName,
    isFirstNameValid,
    isLastNameValid,
    isPrimaryPhoneValid,
    isSecondaryPhoneValid,
    lastName,
    LastName,
    OtherPhoneNumber,
    PhoneNumber,
    Phone1,
    Phone2,
    primaryPhone,
    secondaryPhone,
    state,
    zipCode,
  } = yield select(({ personalInformation }) => ({
    ...personalInformation,
    City: personalInformation.userInfo.Address.City,
    FirstName: personalInformation.userInfo.FirstName,
    IsNameEditable: personalInformation.userInfo.IsNameEditable,
    LastName: personalInformation.userInfo.LastName,
    Line1: personalInformation.userInfo.Address.Line1,
    Line2: personalInformation.userInfo.Address.Line2 || '',
    OtherPhoneNumber: personalInformation.userInfo.OtherPhoneNumber || '',
    Phone1: personalInformation.userInfo.Address.Phone1,
    Phone2: personalInformation.userInfo.Address.Phone2,
    PhoneNumber: personalInformation.userInfo.PhoneNumber,
    StateId: personalInformation.userInfo.Address.StateId,
    ZipCode: personalInformation.userInfo.Address.ZipCode,
  }));

  let body = {};

  switch (type) {
    case 'address':
      body = {
        City: city,
        Line1: address1.trimStart().trimEnd(),
        Line2: address2.trimStart().trimEnd(),
        Phone1: Phone1 || primaryPhone,
        Phone2: Phone2 || secondaryPhone,
        StateId: state,
        ZipCode: zipCode,
      };

      if (body.Phone1 === primaryPhone && !isPrimaryPhoneValid) {
        body.Phone1 = '';
      }

      if (body.Phone2 === secondaryPhone && !isSecondaryPhoneValid) {
        body.Phone2 = '';
      }
      break;

    case 'names':
      body = {
        FirstName: firstName.trimStart().trimEnd(),
        LastName: lastName.trimStart().trimEnd(),
        Phone1: isPrimaryPhoneValid ? primaryPhone : PhoneNumber,
        Phone2: isSecondaryPhoneValid ? secondaryPhone : OtherPhoneNumber,
      };
      break;

    case 'phones':
      body = {
        FirstName: isFirstNameValid ? firstName : FirstName,
        LastName: isLastNameValid ? lastName : LastName,
        Phone1: primaryPhone,
        Phone2: secondaryPhone,
      };
      break;

    default:
      break;
  }
  return body;
}

export function* recoverPassword(dispatch, { payload }) {
  const { email } = payload;
  const url = `${window.location.origin}/`;

  const requestBody = { Email: email, Url: url };

  if (isEmailValid(email)) {
    const data = yield call(
      apiCallWithApiKey,
      'api/Account/PasswordRecoveryMessage',
      {
        body: requestBody,
        headers: IDOR_HEADER_V2,
        method: 'POST',
      },
      dispatch,
    );

    if (data.IsValid) {
      yield call(payload.successCallback);
    } else if (data.Message) {
      yield call(payload.backendErrorCallback, data.Message, true);
    } else {
      yield put(showAlert({ type: MODAL_ALERT_TYPES.ERROR }));
    }
  } else {
    yield call(payload.errorCallback, 'invalidEmail');
  }
}

export function* updateEmail(dispatch, action) {
  const store = yield select(({ accountLogin, common, session }) => ({
    accountLogin,
    common,
    session,
  }));

  const url = 'customer/Auth/UpdateLocalAccountUserInfo';

  const requestBody = {
    id: store.session.userInfo.CustomerId || '',
    newEmail: action.payload.email || store.accountLogin.email.trim(),
  };

  const response = yield call(
    patch,
    dispatch,
    url,
    requestBody,
  );

  if (response.success) {
    yield put(emailUpdated(requestBody));
    yield call(action.payload.successCallback);
  } else {
    yield call(action.payload.errorCallback);
    yield put(updateEmailError('emailTaken'));
  }

  yield put(displayConfirmChangesModal({ showConfirmChangesModal: true }));
}

export function* updatePassword(dispatch, action) {
  const store = yield select(({ accountLogin }) => (accountLogin));
  const requestBody = {
    Email: action.payload.email || store.userInfo.LoginEmail,
    NewPassword: action.payload.newPassword || store.newPassword,
    Password: action.payload.password || store.oldPassword,
  };
  const data = yield call(
    post,
    dispatch,
    'api/User/UpdateEmailAndPassword',
    requestBody,
  );

  if (data.IsValid) {
    yield put(passwordUpdated(requestBody));
    yield call(action.payload.successCallback);
  } else {
    yield put(updatePasswordError({ message: data.Message, type: data.Type }));
    yield call(action.payload.errorCallback, data.Message);
  }
}

export function* updatePetCloudEmail(dispatch, action) {
  const store = yield select(({ accountLogin }) => (accountLogin));
  const requestBody = {
    User: store.petCloudEmail,
  };
  const data = yield call(
    post,
    dispatch,
    'api/User/UpdatePetCloudEmail',
    requestBody,
  );

  if (data.IsValid) {
    yield put(petCloudEmailUpdated(requestBody));
    yield call(action.payload.successCallback);
  } else {
    if (data.Message === 'This user is already taken!') {
      yield put(updatePetCloudEmailError('userTaken'));
    } else {
      yield put(updatePetCloudEmailError(data.Message));
    }

    if (action.payload?.errorCallback) {
      yield call(action.payload.errorCallback, data.Message);
    }
  }
}

export function* updateNames(dispatch, action) {
  const requestBody = yield call(validateHomeDataChanged, 'names');
  const data = yield call(
    post,
    dispatch,
    'api/User/UpdatePersonalInfo',
    requestBody,
  );

  if (data.IsValid) {
    yield put(namesUpdated(requestBody));
    yield call(action.payload.successCallback);
  } else {
    yield put(updateNamesFailed(data.Message));
    yield put(showAlert({ type: MODAL_ALERT_TYPES.ERROR }));

    if (action.payload?.errorCallback) {
      yield call(action.payload.errorCallback);
    }
  }
}

export function* getPaymentHistory(dispatch, action) {
  yield put(paymentHistoryCleared());

  const {
    errorCallback,
    itemsOfFirstPage,
    itemsPerPage,
    pageIndex,
    policyId,
    successCallback,
  } = action.payload;

  const requestBody = {
    itemsOfFirstPage: itemsOfFirstPage || 20,
    itemsPerPage: itemsPerPage || 20,
    pageIndex,
    policyId,
  };

  const response = yield call(
    get,
    dispatch,
    'api/Billing/PaymentHistoryDetails',
    requestBody,
  );

  if (response.IsValid) {
    const paymentHistory = response.Data;

    yield put(paymentHistoryLoaded({
      paymentHistoryCurrentPage: pageIndex,
      paymentHistoryData: paymentHistory,
      paymentHistoryTotalItems: response.TotalItems,
      paymentHistoryTotalPages: response.TotalPages,
    }));

    if (successCallback) {
      yield call(successCallback);
    }
  } else {
    yield put(paymentHistoryLoadFailed(response.Message));

    if (errorCallback) {
      yield call(errorCallback);
    }
  }
}

export function* getPolicyInfo(dispatch, action) {
  const policyNumber = action.payload;
  const response = yield call(
    get,
    dispatch,
    `api/Policy/GetPolicyByPolicyNumber/${policyNumber}`,
  );

  if (response.IsValid) {
    yield put(policyDetailsLoaded(response.Data));
    const paymentPayload = {
      itemsOfFirstPage: 10,
      itemsPerPage: 10,
      pageIndex: 0,
      policyId: response.Data.PolicyId,
    };
    yield put(loadPaymentHistory(paymentPayload));
  } else {
    yield put(policyDetailsLoadFailed(response.Message));
  }
}

export function* getBillingInfo(dispatch, action) {
  const policyNumber = action.payload;
  const response = yield call(
    get,
    dispatch,
    `api/Billing/Info/${policyNumber}`,
  );

  if (response.IsValid) {
    yield put(billingInfoLoaded(response.Data));
  } else {
    yield put(billingInfoLoadFailed(response.Message));
  }
}

function* saveReimbursementMethod({ dispatch, reimbursementData, url }) {
  const response = yield call(postApiV2, dispatch, url, reimbursementData);
  const store = yield select(({ billingPayments, policies }) => ({
    billingPayments, policies,
  }));

  const { billingPayments, policies } = store;

  const policy = policies
    .pets[billingPayments.selectedPet]
    .policies[billingPayments.selectedPolicy];

  if (response.success) {
    yield put(reimbursementMethodUpdated());
    yield put(loadReimbursementMethod(policy.Number));
  } else {
    const errorMessage = response?.message || 'Unexpected Error';
    yield put(reimbursementMethodUpdateFailed(errorMessage));
  }
}

const formatReimbursementBodyRequest = (reimbursementData) => {
  const body = {
    CareCredit: {
      CardNumber: '',
      ExpirationMonth: '',
      ExpirationYear: '',
      FirstName: '',
      LastName: '',
      Type: REIMBURSEMENT_METHODS.none,
    },
    Eft: {
      AccountNumber: '',
      AccountType: 0, // has to be an int
      RoutingNumber: '',
      Type: REIMBURSEMENT_METHODS.none,
    },
  };

  try {
    switch (reimbursementData.Type) {
      case REIMBURSEMENT_METHODS.check:
        body.Eft.Type = REIMBURSEMENT_METHODS.check;

        body.CareCredit = null;
        break;

      case REIMBURSEMENT_METHODS.deposit:
        body.Eft.AccountNumber = reimbursementData.AccountNumber;
        body.Eft.AccountType = reimbursementData.AccountType;
        body.Eft.RoutingNumber = reimbursementData.RoutingNumber;
        body.Eft.Type = REIMBURSEMENT_METHODS.deposit;

        body.CareCredit = null;
        break;

      case REIMBURSEMENT_METHODS.careCredit:
        // main reimbursement method
        body.CareCredit.CardNumber = reimbursementData.careCredit.cardNumber;
        body.CareCredit.ExpirationMonth = reimbursementData
          .careCredit.expDate.split('/')[0];
        body.CareCredit.ExpirationYear = reimbursementData
          .careCredit.expDate.split('/')[1].slice(-2);
        body.CareCredit.FirstName = reimbursementData.careCredit.firstName;
        body.CareCredit.LastName = reimbursementData.careCredit.lastName;
        body.CareCredit.Type = REIMBURSEMENT_METHODS.careCredit;

        // secondary reimbursement method
        body.Eft.AccountNumber = reimbursementData
          .secondaryMethod.AccountNumber;
        body.Eft.AccountType = reimbursementData.secondaryMethod.AccountType;
        body.Eft.RoutingNumber = reimbursementData
          .secondaryMethod.RoutingNumber;
        body.Eft.Type = reimbursementData.secondaryMethod.Type;
        break;

      default:
        break;
    }
  } catch {
    // do nothing, just return the default body initial state :P
  }

  return body;
};

export function* updateReimbursementMethod(dispatch, { payload }) {
  const reimbursementDataBody = formatReimbursementBodyRequest(payload);
  const useClaimsApiV1 = yield useClaimsApiV1ForSagas();
  const useCareCredit = yield usePetCloudCareCreditReimbursementForSagas();
  const useReimbursementVerification =
    yield usePetCloudRequireReimbursementVerificationForSagas();

  const url = useCareCredit
    ? 'claims/Claims/SaveReimbursementMethods'
    : 'claims/reimbursement-methods';

  const reimbursementData = useCareCredit
    ? reimbursementDataBody
    : reimbursementDataBody.Eft;

  if (useReimbursementVerification) {
    const store = yield select(({ otpVerification }) => (otpVerification));
    const { verificationToken } = store.verificationCode;
    reimbursementDataBody.Eft.VerificationToken = verificationToken;
  }

  const routingNumberValidation = [
    REIMBURSEMENT_METHODS.deposit,
    REIMBURSEMENT_METHODS.careCredit,
  ];

  if (routingNumberValidation.includes(reimbursementDataBody.Eft.Type)) {
    const routingNumber = reimbursementDataBody.Eft.RoutingNumber;
    const routingNumberUrl = useClaimsApiV1
      ? `claims/claims/ValidateACHRoute/${routingNumber}`
      : `/api/Claim/ValidateACHRoute/${routingNumber}`;

    const routingNumberResponse = yield call(
      get,
      dispatch,
      routingNumberUrl,
    );
    if (routingNumberResponse.IsValid) {
      yield call(saveReimbursementMethod, { dispatch, reimbursementData, url });
    } else {
      yield put(
        reimbursementMethodRoutingValidationFailed(
          routingNumberResponse.Message,
        ),
      );
    }
  } else {
    yield call(saveReimbursementMethod, { dispatch, reimbursementData, url });
  }

  // reset verification token after use
  yield put(verifyCodeReset());
}

export function* updatePaymentMethod(dispatch, action) {
  const {
    paymentMethodData,
    policies,
    stripePaymentData,
  } = action.payload;
  const usePetCloudStripe = yield usePetCloudStripeT177101ForSagas();
  const useUpdatePaymentMethodT215309 =
    yield usePetCloudUpdatePaymentMethodT215309ForSagas();
  yield put(paymentMethodUpdateAttempted());

  let policyNumber = paymentMethodData?.policyNumber;

  let dataCreditCard = {};
  let dataAcc = {};
  let data = {};

  if (usePetCloudStripe) {
    policyNumber = stripePaymentData?.policyNumber;
    // stripe
    data = {
      oneIncCreditCardDTO: {},
      OneIncEftDTO: dataAcc,
      StripePaymentDto: {
        Token: stripePaymentData?.tokenId,
      },
    };
  } else {
    // oneinc
    const methodData = paymentMethodData || {};
    const isCreditCard = methodData.paymentCategory === 'CreditCard';

    dataCreditCard = {
      cardExpirationMonth: methodData.cardExpirationMonth,
      cardExpirationYear: methodData.cardExpirationYear,
      cardholderName: methodData.customerName,
      cardType: methodData.cardType,
      lastFourDigits: methodData.lastFourDigits,
      tokenId: methodData.tokenId,
    };
    dataAcc = {
      LastFourDigits: methodData.lastFourDigits,
      TokenId: methodData.tokenId,
    };

    data = {
      oneIncCreditCardDTO: isCreditCard ? dataCreditCard : {},
      OneIncEftDTO: isCreditCard ? {} : dataAcc,
      paymentMethodEnum: isCreditCard ? 1 : 0,
    };
  }

  const service = useUpdatePaymentMethodT215309 ? 'purchase' : 'api/';
  const url = `${service}/Payments/UpdatePaymentMethod/${policyNumber}`;

  const actionService = useUpdatePaymentMethodT215309 ? postApiV2 : postV2;

  const returnObject = 'json';
  const useGenericError = false;

  const policyResponse = yield call(
    actionService, dispatch, url, data, returnObject, useGenericError,
  );
  const error = policyResponse.message;
  const messageResponse = policyResponse.message;

  yield put(stripeUpdatePaymentMethodError(messageResponse));

  yield put(closeMultiPoliciesModal());
  yield put(paymentMethodPastDueModalVisibleChanged({ visible: false }));
  yield put(loadPoliciesPaymentNumber());
  yield put(loadPolicyDetails(policyNumber));
  yield call(getPolicySummary, dispatch);
  yield put(paymentMethodUpdated({
    error,
    messageResponse,
    policiesUpdated: policies.length,
  }));
}

export function* doPetLoad(dispatch) {
  yield put(petsLoadAttempt());

  const userInfo = yield call(getStorageOrDefault, USER_INFO_KEY);

  if (!userInfo) {
    return;
  }

  const useCustomerPets = yield useCustomerApi150617V1ForSagas();
  const urlPref = useCustomerPets ? 'customer' : 'api';

  const { SocialInfo } = userInfo;

  const url = `${urlPref}/Pet?UnaId=${SocialInfo.Id}`;

  const petsResponse =
    yield call(get, dispatch, url);

  if (petsResponse.IsValid) {
    yield put(petsLoaded(petsResponse.Data));
  }

  yield put(petsLoadedFinished());
}

export function* doSocialProfileLoad(dispatch) {
  yield put(socialProfileLoadAttempt());
  const store = yield select(({ accountLogin }) => (accountLogin));

  if (!store.userInfo) {
    return;
  }

  const { userInfo: { SocialInfo } } = store;

  const url = `api/Social/Profile/${SocialInfo.Id}`;

  const profileResponse =
    yield call(get, dispatch, url);

  if (profileResponse.IsValid) {
    yield put(socialProfileLoaded(profileResponse.Data));
  }

  yield put(socialProfileLoadedFinished());
}

// isCover: true -> Cover, false -> ProfilePicture
function* changeProfilePicture(dispatch, { payload }) {
  const { imageBase64, isCover } = payload;
  const error = 'Unexpected error saving photo.';
  const postBody = {
    ContentType: 'image/jpeg',
    Extension: 'jpg',
    FileContent: imageBase64,
    ImageType: isCover
      ? IMAGE_UPLOAD_TYPE.background
      : IMAGE_UPLOAD_TYPE.customerProfile,
  };

  // uploads the image
  const uploadImageResponse = yield call(
    post,
    dispatch,
    'api/Social/Upload',
    postBody,
  );

  if (uploadImageResponse.success && uploadImageResponse.IsValid) {
    const { Data } = uploadImageResponse;
    // links the image to the user
    const linkImageToUserResponse = yield call(
      post,
      dispatch,
      'api/Social/Pictures/Submit',
      {
        CoverPictureUid: isCover ? Data : null,
        ProfilePictureUid: isCover ? null : Data,
      },
    );

    if (linkImageToUserResponse.success && linkImageToUserResponse.IsValid) {
      // waits for the cache refresh time.
      yield put(clearUserCache());
      yield delay(5000);
      yield put(changeProfilePictureSuccess());
    } else {
      yield put(changeProfilePictureFailed(error));
    }
  } else {
    yield put(changeProfilePictureFailed(error));
  }
}

function* removeCoverPicture(dispatch) {
  const response = yield call(
    del,
    dispatch,
    'api/Social/User/Cover/Picture',
  );

  if (response.IsValid) {
    yield put(coverPictureRemoved());
  } else {
    yield put(removeCoverPictureFailed());
  }
}

function* removeAvatarPicture(dispatch) {
  const response = yield call(
    del,
    dispatch,
    'api/Social/User/Profile/Picture',
  );

  if (response.IsValid) {
    yield put(clearUserCache());
    yield put(avatarPictureRemoved());
  } else {
    yield put(removeAvatarPictureFailed());
  }
}

export function* updateInsuranceInformation(dispatch, action) {
  const data = action.payload;
  const response = yield call(
    post,
    dispatch,
    'api/Account/ChangeAccountInformation',
    data,
  );
  if (response.IsValid) {
    yield put(updateInsuranceInformationSuccess());
  } else {
    yield put(updateInsuranceInformationFailed());
  }
}

function* getFeatureFlags() {
  try {
    const response = yield call(apiCallWithApiKey, 'customer/FeatureFlag');
    const petCloudFlags = Object.entries(response)
      .filter(([key]) => key.startsWith('PetCloud'))
      .reduce((p, [k, v]) => ({ ...p, [k]: v }), {});
    yield put(getFeatureFlagsLoaded(petCloudFlags));
  } catch (error) {
    yield put(getFeatureFlagsFailed(error));
  }
}

export function* settingsSaga(store) {
  yield all([
    takeLatest(CHANGE_PROFILE_PICTURE, changeProfilePicture, store.dispatch),
    takeLatest(REMOVE_COVER_PICTURE, removeCoverPicture, store.dispatch),
    takeLatest(REMOVE_AVATAR_PICTURE, removeAvatarPicture, store.dispatch),
    takeLatest(
      UPDATE_INSURANCE_INFORMATION,
      updateInsuranceInformation,
      store.dispatch,
    ),
    takeLatest(GET_FEATURE_FLAGS, getFeatureFlags, store.dispatch),
  ]);
}
